import type { RefObject } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import Sortable from 'sortablejs';

export interface SortEnd {
  itemId: string;
  fromId: string;
  toId: string;
  newIndex: number;
}

/**
 * SortableJS on a container while `enabled`. Sortable moves DOM nodes itself,
 * which would confuse Preact, so on drop the node is put back and `onEnd`
 * applies the move to state instead; Preact then re-renders the new order.
 * Items need `data-id`; containers need `data-container-id`.
 */
export function useSortable(
  ref: RefObject<HTMLElement>,
  enabled: boolean,
  options: { group?: string; handle?: string; draggable: string },
  onEnd: (e: SortEnd) => void
) {
  const onEndRef = useRef(onEnd);
  onEndRef.current = onEnd;

  useEffect(() => {
    if (!enabled || !ref.current) return;
    const sortable = Sortable.create(ref.current, {
      group: options.group,
      handle: options.handle,
      draggable: options.draggable,
      animation: 150,
      ghostClass: 'drag-ghost',
      chosenClass: 'drag-chosen',
      fallbackOnBody: true,
      onEnd(evt) {
        const { item, from, to, oldIndex, newIndex } = evt;
        if (oldIndex === undefined || newIndex === undefined) return;
        if (from === to && oldIndex === newIndex) return;
        to.removeChild(item);
        from.insertBefore(item, from.children[oldIndex] ?? null);
        onEndRef.current({
          itemId: item.dataset.id!,
          fromId: from.dataset.containerId!,
          toId: to.dataset.containerId!,
          newIndex,
        });
      },
    });
    return () => sortable.destroy();
  }, [enabled, ref, options.group, options.handle, options.draggable]);
}
